
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Job } from './types';
import { format, parseISO } from 'date-fns';
import { Pencil, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteButton } from "@/components/common/DeleteButton";

interface JobCardProps {
  job: Job;
}

export const JobCard: React.FC<JobCardProps> = ({ job }) => {
  const navigate = useNavigate();

  return (
    <Card className="flex flex-col h-full">
      <CardHeader onClick={() => navigate(`/jobs/${job.id}`)} className="cursor-pointer">
        <CardTitle>{job.title}</CardTitle>
        <CardDescription>{job.company || job.client_name || 'N/A'} - {job.location}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 flex-grow cursor-pointer" onClick={() => navigate(`/jobs/${job.id}`)}>
        <div className="flex justify-between items-center text-sm">
          <span>Status:</span>
          <Badge variant={
            job.status === 'open' ? 'default' :
            job.status === 'closed' ? 'destructive' :
            job.status === 'on-hold' ? 'secondary' :
            'outline'
          }>
            {job.status?.replace('-', ' ')}
          </Badge>
        </div>
        <div className="text-sm">Type: {job.employment_type || 'N/A'}</div>
        <div className="text-sm">Posted: {job.posted_date ? format(parseISO(job.posted_date), 'MMM d, yyyy') : 'N/A'}</div>
        <div className="text-sm">Applicants: {job.applicants ?? 0}</div>
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        <Button variant="outline" size="sm" className="flex-grow" onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.id}`); }}>
          View Details
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-2">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/jobs/${job.id}#overview-edit`)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Job
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <DeleteButton
                entityType="job"
                entityId={job.id}
                entityName={job.title || 'Untitled Job'}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-destructive hover:text-destructive"
              >
                Delete Job
              </DeleteButton>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardFooter>
    </Card>
  );
};
